<template>
  <USlideover
    v-model:open="isNotificationsSlideoverOpen"
    title="Notifications"
  >
    <template #header>
      <div class="flex items-center justify-between w-full">
        <h3 class="text-lg font-semibold">
          Notifications
        </h3>
        <div class="flex items-center gap-2">
          <span class="text-xs text-muted">Tout afficher</span>
          <UCheckbox
            v-model="showArchived"
            @update:model-value="loadNotifications"
          />
          <UButton
            icon="i-lucide-x"
            color="neutral"
            variant="ghost"
            size="sm"
            @click="isNotificationsSlideoverOpen = false"
          />
        </div>
      </div>
    </template>

    <template #body>
      <div
        v-if="fetchError"
        class="text-center py-8"
      >
        <Icon
          name="lucide:alert-circle"
          class="mb-2 text-error text-2xl"
        />
        <p class="text-sm text-error font-medium mb-2">
          Impossible de charger les notifications
        </p>
        <p class="text-xs text-muted mb-4">
          {{ fetchError.message || 'Une erreur est survenue' }}
        </p>
        <UButton
          size="sm"
          @click="() => refresh()"
        >
          Réessayer
        </UButton>
      </div>

      <div
        v-else-if="pending"
        class="text-center py-8"
      >
        <Icon
          name="lucide:loader-2"
          class="mb-2 text-muted text-2xl animate-spin"
        />
        <p class="text-sm text-muted">
          Chargement des notifications...
        </p>
      </div>

      <div
        v-else-if="notifications && notifications.length > 0"
        class="space-y-1"
      >
        <div
          v-for="notification in notifications"
          :key="notification.id"
          class="px-3 py-2.5 rounded-md hover:bg-elevated/50 flex items-center gap-3 relative -mx-3 first:-mt-3 last:-mb-3 group"
          :class="{ 'opacity-60': archivingId === notification.id }"
        >
          <UChip
            color="error"
            :show="notification.status === 'inbox'"
            inset
            class="shrink-0 cursor-pointer"
            @click="openNotificationDetail(notification)"
          >
            <UAvatar
              :src="getSenderAvatarUrl(notification.sender?.avatar)"
              :alt="notification.sender ? `Avatar de ${notification.sender.first_name} ${notification.sender.last_name}` : 'Avatar par défaut'"
              size="md"
            />
          </UChip>

          <div
            class="text-sm flex-1 min-w-0 cursor-pointer"
            @click="openNotificationDetail(notification)"
          >
            <div class="flex items-center justify-between gap-2 mb-0.5">
              <span class="text-highlighted font-medium truncate">
                {{ notification.subject }}
              </span>

              <time
                :datetime="notification.timestamp"
                class="text-muted text-xs shrink-0"
                v-text="formatTimeAgoIntl(new Date(notification.timestamp), { locale: 'fr-BE' })"
              />
            </div>

            <p class="text-dimmed text-xs truncate">
              {{ notification.message }}
            </p>
          </div>

          <UButton
            v-if="notification.status !== 'archived'"
            :icon="archivingId === notification.id ? 'i-lucide-loader-2' : 'i-lucide-check'"
            :class="[
              'transition-opacity shrink-0',
              { 'animate-spin': archivingId === notification.id },
              archivingId === notification.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
            ]"
            color="neutral"
            variant="ghost"
            size="xs"
            :disabled="archivingId !== null"
            @click="archiveNotification(notification.id)"
          />
        </div>

        <div
          v-if="hasMore"
          class="pt-2 flex justify-center"
        >
          <UButton
            label="Charger plus"
            color="neutral"
            variant="ghost"
            size="sm"
            :loading="loadingMore"
            @click="loadMore"
          />
        </div>
      </div>

      <div
        v-else
        class="text-center py-8 text-muted"
      >
        <p>Aucune notification</p>
      </div>
    </template>
  </USlideover>

  <!-- Slideover de détail de notification -->
  <USlideover
    v-model:open="isDetailSlideoverOpen"
    title="Détail de la notification"
  >
    <template
      v-if="selectedNotification"
      #body
    >
      <div class="space-y-6 -mx-3 -mt-3">
        <!-- Header avec avatar et infos -->
        <div class="flex items-start gap-3 px-3 py-4 bg-elevated/50">
          <UChip
            color="error"
            :show="selectedNotification.status === 'inbox'"
            inset
            class="shrink-0"
          >
            <UAvatar
              :src="getSenderAvatarUrl(selectedNotification.sender?.avatar)"
              :alt="selectedNotification.sender ? `Avatar de ${selectedNotification.sender.first_name} ${selectedNotification.sender.last_name}` : 'Avatar par défaut'"
              size="lg"
            />
          </UChip>

          <div class="flex-1 min-w-0">
            <h3 class="text-lg font-semibold text-highlighted mb-1">
              {{ selectedNotification.subject }}
            </h3>
            <p class="text-sm text-muted">
              De {{ selectedNotification.sender ? `${selectedNotification.sender.first_name ?? ''} ${selectedNotification.sender.last_name ?? ''}` : 'Wall-E' }}
            </p>
            <p class="text-xs text-dimmed">
              {{ formatTimeAgoIntl(new Date(selectedNotification.timestamp), { locale: 'fr-BE' }) }}
            </p>
          </div>
        </div>

        <!-- Message -->
        <div class="px-3">
          <p class="text-sm whitespace-pre-wrap leading-relaxed">
            {{ selectedNotification.message }}
          </p>
        </div>

        <!-- Lien contextuel -->
        <div
          v-if="getNotificationLink(selectedNotification)"
          class="px-3"
        >
          <UButton
            :to="getNotificationLink(selectedNotification)"
            label="Voir la ressource associée"
            icon="i-lucide-external-link"
            color="primary"
            variant="soft"
            block
            @click="isDetailSlideoverOpen = false"
          />
        </div>

        <!-- Actions -->
        <div
          v-if="selectedNotification.status !== 'archived'"
          class="px-3 pb-3 flex gap-2"
        >
          <UButton
            label="Archiver"
            icon="i-lucide-archive"
            color="neutral"
            block
            :loading="archivingId === selectedNotification.id"
            @click="archiveNotificationFromSlideover(selectedNotification.id)"
          />
        </div>
      </div>
    </template>
  </USlideover>

  <!-- Audio pour le son de notification -->
  <audio
    ref="notificationSound"
    preload="auto"
  >
    <source
      src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIGGS66+yhUhENUKvl8b1iHAU2kdfw0H4yBSp7zu/gkz4LElyw6OyrWBUIQ5zd8sFuJAYuhM/z24k7CBllu+rtnGIZDkyp5O+1YiAGNpLX8c5+MQUpetDu45A+CxJbsOftqVkVB0Kb3fDCbiMFLoLP8tyJOggZZbzp7aFOEQ1OquTwt2EdBjWS1/HQfjEFKXrP7+STOwsVXa/o7KpaFQdCmt3wwW4jBS2Cz/LciToIGWK86O2gUREOTKnk8LZhHwY2kdfx0H4xBSl70O/jkz4LElyw5+ypWBUGQ5zd8MFuJAYuhc7y24k6CBlmu+rtjEgNDEqp5fC2Yh4GNZLX8M5+MAUpedDv45M+CxRcr+fsqloVB0Oa3fDBbiQGLYTO8tuKOwgZZbvq7Z1hGQxLqeTuuWEfBzWS1/HOfjAFKXrP7+OTPgsUXK/n7KlaFQZDm93wwm4kBi6EzvLbijsIGWW86u2eYRkMTKjk7rdhHgU1ktfw0H4wBSh6z+/jlD4LElyv5+ypWhUHQprd8MJuJAUthc7y3Io7CBllu+rtnoNaGg9HqeXwtmIfBjSS1/DPfjEFKXrP7+SSPgsUXK/n66laFAdCmt3wwm4kBS2EzvLcijsIGWS76u2fYRkLS6nk77lhHgY1ktfwzn4xBSh5z+/jlD4LEV2v6OyqWhUHQ5rd8MJuIwUtg87y3Is7CBlmu+rtoVESDU2o5PC3YR8GNZLX8c5+MQUpetDu45M+CxRbsOjsq1oVBkOa3fDBbiQFLYTO8tyKOwgZZLzp7aFREg5MqeTwt2EeBjWS1/HQfjEFJ3rP7+STOwsUXK/o7KtaFQZDm93wwW4jBS2DzvLcizsIGWW76u2hURQOTKnk8LdhHwY1kdfw0H4xBSh6z+/jlD4LFFyv6OyrWhUGQ5rd8MFuIwUtg87y3Io7CBllu+rtolESDkyp5PC2YR8GNZLX8dB+MQUpes/v5JQ9CxVcsOjsq1sVBkOa3fDBbiQFLYTO8tuKOwgZZbvq7aJREg5NqeTvt2EfBjWS1/HQfjEFKHrP7+SUPQsUXK/o7KtaFQZDmt3wwW4kBS2EzvLbijsIGWW76u2iURQOTKnk8LdhHwY1ktfw0H4xBSh6z+/klD0LFFyv6OyrWhUGQ5vd8MFuJAUtg87y24o7CBllu+rtolESDkyp5PC3YR8GNZLX8dB+MQUoes/v5JQ9CxVcsOjsq1oVBkOa3fDBbiQFLYPO8tuKOwgZZbvq7aJSEg5NqeTvt2EfBjWS1/HQfjEFKXrP7+SUPQsUXK/o7KtbFQZCmt3wwW4kBS2EzvLbijsIGWW76u2iURMOTank77dhHwY1ktfw0H4xBSh6zu/klD4LElyv6OyrWhUGQ5rd8MFuJAYug87y24o7CBlmu+rtolESDU2o5O+3YSAGNpLX8dB+MQUpes/v5JQ+CxVcsOjsq1sVBkOa3fDBbiQFLYPO8tuKOwgZZbvq7aJSEg5NqeTut2EfBjWS1/HQfjEFKHrQ7+SUPgsUXa/o7KpaFQdCmt3xwW4jBS6DzvLciToIGWW76u2iURIOS6nl8LdhHwY2ktfx0H4xBSh60O/klD4LFFyv6OyrWhUHQprd8MJuJAUtg87y3Io7CBlkv+rtnlESDE2p5O+3YR8GNZLXz9B+MAUpedDv45Q+CxVdr+jsqlsVB0Ka3fDBbiQGLYTO8tyKOwgZZb3q7Z5RGwxMqeT"
      type="audio/wav"
    >
  </audio>
</template>

<script lang="ts" setup>
import { formatTimeAgoIntl } from '@vueuse/core'
import { useBugsnagReporter } from '~/composables/useBugsnagReporter'

const { isNotificationsSlideoverOpen, notifications: dashboardNotifications } = useDashboard()
const showArchived = ref(false)
const notificationSound = ref<HTMLAudioElement | null>(null)
const archivingId = ref<string | null>(null)
const isDetailSlideoverOpen = ref(false)
const selectedNotification = ref<any>(null)
const toast = useToast()
const config = useRuntimeConfig()
const { notifyError } = useBugsnagReporter()

const fallbackAvatarUrl = 'https://api.dicebear.com/9.x/adventurer-neutral/svg?seed=Wall-E&backgroundType=gradientLinear&backgroundRotation=-320&backgroundColor=b6e3f4,c0aede'
const directusBaseUrl = config.public.directus.url || 'https://api.ambulancepresent.be'

const getSenderAvatarUrl = (avatarId?: string | null) => {
  if (!avatarId) return fallbackAvatarUrl
  return `${directusBaseUrl}/assets/${avatarId}`
}

// Routes contextuelles par collection Directus
const COLLECTION_ROUTES: Record<string, string> = {
  orders_v2: '/volontaires/commandes',
}

const getNotificationLink = (notification: any): string | undefined => {
  if (!notification?.collection || !notification?.item) return undefined
  return COLLECTION_ROUTES[notification.collection]
}

// Marquer une notification comme lue
const markAsRead = async (notification: any) => {
  if (notification.status !== 'inbox') return
  try {
    await $fetch(`/api/notifications/${notification.id}`, {
      method: 'PATCH',
      body: { status: 'read' },
    })
    notification.status = 'read'
    if (selectedNotification.value?.id === notification.id) {
      selectedNotification.value = { ...notification }
    }
    dashboardNotifications.value = [...notifications.value]
  }
  catch (error: any) {
    console.error('Erreur lors du marquage comme lu:', error)
  }
}

// Ouvrir le détail d'une notification
const openNotificationDetail = async (notification: any) => {
  selectedNotification.value = notification
  isDetailSlideoverOpen.value = true
  await markAsRead(notification)
}

const LIMIT = 20
const notifications = ref<any[]>([])
const hasMore = ref(false)
const pending = ref(false)
const loadingMore = ref(false)
const fetchError = ref<any>(null)
const offset = ref(0)

const refresh = async () => {
  pending.value = true
  fetchError.value = null
  try {
    const data = await $fetch<any[]>('/api/notifications', {
      query: { showArchived: showArchived.value, limit: LIMIT, offset: 0 },
    })
    notifications.value = data
    offset.value = data.length
    hasMore.value = data.length === LIMIT
  }
  catch (e: any) {
    fetchError.value = e
  }
  finally {
    pending.value = false
  }
}

const loadMore = async () => {
  loadingMore.value = true
  try {
    const data = await $fetch<any[]>('/api/notifications', {
      query: { showArchived: showArchived.value, limit: LIMIT, offset: offset.value },
    })
    notifications.value.push(...data)
    offset.value += data.length
    hasMore.value = data.length === LIMIT
  }
  catch {
    // échec silencieux pour le chargement supplémentaire
  }
  finally {
    loadingMore.value = false
  }
}

const loadNotifications = () => refresh()

watch(notifications, (newNotifications) => {
  dashboardNotifications.value = newNotifications ?? []
})

// Afficher l'erreur de chargement des notifications
watch(fetchError, (newError) => {
  if (newError) {
    notifyError(newError, {
      context: 'notifications.fetch',
    })
    console.error('Erreur lors du chargement des notifications:', newError)
    toast.add({
      title: 'Récupération des notifications échouée',
      description: newError.message || 'Une erreur est survenue',
      color: 'error',
      icon: 'i-lucide-alert-circle',
    })
  }
}, { immediate: true })

// Fonction pour archiver une notification
const archiveNotification = async (id: string) => {
  if (archivingId.value) return

  archivingId.value = id

  try {
    await $fetch(`/api/notifications/${id}`, {
      method: 'PATCH',
      body: {
        status: 'archived',
      },
    })

    // Rafraîchir la liste
    await refresh()

    toast.add({
      title: 'Notification archivée',
      color: 'success',
      icon: 'i-lucide-check',
    })
  }
  catch (error: any) {
    notifyError(error, {
      context: 'notifications.archive',
      notificationId: id,
      serverMessage: error?.data?.message ?? error?.message,
      ...error?.data,
    })
    console.error('Erreur lors de l\'archivage:', error)
    toast.add({
      title: 'Erreur',
      description: 'Impossible d\'archiver la notification',
      color: 'error',
      icon: 'i-lucide-x',
    })
  }
  finally {
    archivingId.value = null
  }
}

// Fonction pour archiver depuis le slideover
const archiveNotificationFromSlideover = async (id: string) => {
  await archiveNotification(id)
  isDetailSlideoverOpen.value = false
  selectedNotification.value = null
}

// Polling pour vérifier les nouvelles notifications
let previousNotificationIds: string[] = []
let pollingInterval: ReturnType<typeof setInterval> | null = null

const checkNewNotifications = async () => {
  if (!notifications.value) return

  // Ne considérer que les notifications inbox (non lues)
  const inboxNotifications = notifications.value.filter((n: any) => n.status === 'inbox')
  const currentIds: string[] = inboxNotifications.map((n: any) => String(n.id))

  // Vérifier s'il y a de nouvelles notifications inbox
  const newNotifications = currentIds.filter(id => !previousNotificationIds.includes(id))

  if (newNotifications.length > 0 && previousNotificationIds.length > 0) {
    // Jouer le son
    if (notificationSound.value) {
      try {
        await notificationSound.value.play()
      }
      catch (error) {
        notifyError(error, {
          context: 'notifications.sound.play',
        })
        console.error('Impossible de jouer le son:', error)
      }
    }

    // Afficher une notification
    toast.add({
      title: 'Nouvelle notification',
      description: `Vous avez ${newNotifications.length} nouvelle(s) notification(s)`,
      color: 'primary',
      icon: 'i-lucide-bell',
    })
  }

  previousNotificationIds = currentIds
}

// Direct : une nouvelle notification dans la boîte recharge la liste (son et toast via la surveillance ci-dessous)
useLiveNotifications(() => {
  void refresh()
})

// Chargement initial + polling lent en filet de sécurité (le direct WebSocket fait le gros du travail)
onMounted(async () => {
  await refresh()

  const inboxNotifications = notifications.value.filter((n: any) => n.status === 'inbox')
  previousNotificationIds = inboxNotifications.map((n: any) => String(n.id))

  pollingInterval = setInterval(async () => {
    if (document.hidden) return
    await refresh()
  }, 60000)
})

// Nettoyer l'intervalle
onUnmounted(() => {
  if (pollingInterval) {
    clearInterval(pollingInterval)
  }
})

// Vérifier les nouvelles notifications quand les données changent
watch(notifications, () => {
  checkNewNotifications()
})
</script>
